__webpack_public_path__ =
  process.env.ENV === 'development'
    ? process.env.WEBPACK_PUBLIC_PATH
    : `${window.$mediavine.web.model.wrapperSrc}/`

import { AuctionHouse } from './auctionHouse/AuctionHouse'
import Blocklist from './blocklist/Blocklist'
import { domReady, writeCss, addClass } from './helpers/DOM'
import Logger from './helpers/Logger'
import WebModel, {
  optimizingForDesktopPagespeed,
  optimizingForMobilePagespeed
} from './models/WebModel'
import PageSettings from './pageSettings/PageSettings'
import DynamicContentSlots from './renderers/DynamicContentSlots'
import SlotFactory from './slots/SlotFactory'
import StickyVideo from './stickyVideo/StickyVideo'
import { getBaseStyles } from './styles/base'
import { AdReporter } from './adReporter/AdReporter'
import { doOnce } from './helpers/doOnce'
import { polyfill } from './polyfills/polyfills'
import JumpToRecipe from './jumpToRecipe/JumpToRecipe'
// import { Session } from './session/Session'
// import { UTM } from './helpers/UTM'
import { Flooring } from './flooring/Flooring'
import { ArrivalSlot } from './renderers/ArrivalSlot'
import { USP } from './cmp/usp/USP'
import MetricsTracker from './metrics/MetricsTracker'
import GrowFaves from './growFaves/GrowFaves'

export const GDPRStub: IGDPR = {
  enableGdpr: false,
  onReady: (cb) => cb(),
  initModal: () => {}
}

if (window.$mediavine.web.model.gdpr === '1') {
  import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TcfStub')
    .then(({ makeStub }) => makeStub())
    .catch((error) => {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    })
}

const debug = (msg: string) => Logger.custom(['debug', 'wrapper'], msg)

let model: WebModel
let ccpa
let gdpr: IGDPR = GDPRStub

const reRenderables: Array<{ render: Function }> = []

domReady(async () => {
  const model = WebModel.applyLocalModel(window.$mediavine.web.model)
  StickyVideo.setVideoTargetDivs(model)
  window.$mediavine.web.videoOptionsSet = true
})

export enum ExecutionStatus {
  halted,
  delayed,
  executed
}

export const init = async (): Promise<ExecutionStatus> => {
  if (window.$mediavine.web.initialized) {
    debug('Wrapper previously initialized. Halting Execution.')

    return ExecutionStatus.halted
  }

  try {
    await polyfill()
  } catch (e) {
    console.warn(e)
  }
  domReady(() => {
    addClass(document.body, 'mv-loaded')
  })
  window.$mediavine.web.initialized = true

  window.$mediavine.web.load = doOnce(async () => {
    const { load } = await import('./afterScroll')
    return load(
      model,
      blocklist,
      slotFactory,
      auctionHouse,
      gdpr,
      reRenderables
    )
  })

  new AdReporter()

  await Flooring.requestGeolocation(window.$mediavine.web.model)

  // Flooring.requestGeolocation MUST be called before new WebModel.
  model = new WebModel(window.$mediavine.web.model)
  if (euNoAds(model)) {
    debug('Wrapper not inited because site does not serve ads in EU')

    return ExecutionStatus.halted
  }

  new MetricsTracker(model)
  const geoLoc = await Flooring.requestGeolocation(window.$mediavine.web.model)
  model.geoLoc = geoLoc

  if (model.gdpr === '1') {
    try {
      const [{ TCF }, { GDPR }] = await Promise.all([
        import(/* webpackChunkName: "gdpr"*/ './cmp/tcf/TCF'),
        import(/* webpackChunkName: "gdpr"*/ './cmp/gdpr/GDPR')
      ])
      await TCF.init()
      gdpr = new GDPR()
      await gdpr.initModal()
    } catch (error) {
      const errorMessage = 'GDPR: Error loading "gdpr" webpackChunk'
      Logger.custom(['debug', 'gpdr'], errorMessage)
      window.$mediavine.newrelic.addPageAction('gdprError', {
        error,
        errorMessage
      })
    }
  } else {
    window.dispatchEvent(
      new CustomEvent('mediavineCmp', { detail: 'gdprDoesNotApply' })
    )
  }

  USP.init(model)

  if (model.ccpa === '1') {
    try {
      const [{ CCPA }] = await Promise.all([
        import(/* webpackChunkName: "ccpa"*/ './cmp/ccpa/CCPA')
      ])
      ccpa = new CCPA(model)
      await ccpa.initModal()
    } catch (error) {
      const errorMessage = 'CCPA: Error loading "ccpa" webpackChunk'
      Logger.custom(['debug', 'ccpa'], errorMessage)
      window.$mediavine.newrelic.addPageAction('ccpaError', {
        error,
        errorMessage
      })
    }
  }

  writeCss(getBaseStyles(model))
  const pageSettings = new PageSettings()
  const blocklist = new Blocklist(model, pageSettings)
  const auctionHouse = new AuctionHouse(model)
  const slotFactory = new SlotFactory(model, blocklist, auctionHouse)
  new GrowFaves(model)
  reRenderables.push(new DynamicContentSlots(model, slotFactory))
  reRenderables.push(new StickyVideo(model, blocklist))
  ArrivalSlot.attachListener(model, window.$mediavine.web.load)

  new JumpToRecipe(model)

  if (optimizingForMobilePagespeed() || optimizingForDesktopPagespeed()) {
    debug('Pagespeed Optimization. Delaying execution until scroll.')

    const callback = () => {
      debug('Scroll detected or waited too long. Executing.')
      window.removeEventListener('scroll', callback, false)
      window.removeEventListener('beforeprint', callback, false)

      window.$mediavine.web.load()
    }

    window.addEventListener('scroll', callback, false)
    window.addEventListener('beforeprint', callback, false)

    // Alternatively, force load after 3 seconds of waiting for scroll
    // const loadAfterMs = 3100 //ms
    // setTimeout(callback, loadAfterMs)

    return ExecutionStatus.delayed
  } else {
    window.$mediavine.web.load()

    return ExecutionStatus.executed
  }
}
window.$mediavine.web.init = init

const euNoAds = (model: WebModel): boolean => {
  const isEU = model.gdpr === '1'
  const gdprNonCompliant = model.gdpr_compliance !== 1
  return isEU && gdprNonCompliant
}

export const validate = (bool: boolean, errMessage: string): boolean => {
  if (bool) {
    debug(`${errMessage}. Halting execution.`)
  }
  return bool
}

export const onPrivacyPolicyPage = (model): boolean =>
  validate(
    model.privacy_policy_link ===
      `${window.location.origin}${window.location.pathname}`,
    "On site's privacy page."
  )
